// 2P AI — Utilizzo (R5): token e costo registrati da 2P AI, per periodo e per modello.
// Risponde alla richiesta "una vista come la funzione utilizzo di Claude". Mostra il consumo
// passato per questo pannello; i limiti del piano Anthropic vivono lato SDK (/usage sperimentale).
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { CommandBar } from '../components/CommandBar';
import { Crumb } from '../components/Crumb';
import { Icon } from '../components/Icon';

interface Bucket {
  input: number; output: number; cache_read: number; cache_creation: number;
  tokens: number; cost: number; sessions: number;
}
interface Win { utilization: number | null; resets_at: string | null }
interface UsageData {
  plan?: { five_hour?: Win | null; seven_day?: Win | null } | null;
  totals: { today: Bucket; week: Bucket; month: Bucket; all: Bucket };
  by_model: (Bucket & { model: string })[];
}

const fmt = (n: number): string =>
  n >= 1e9 ? (n / 1e9).toFixed(2) + 'B' : n >= 1e6 ? (n / 1e6).toFixed(2) + 'M' : n >= 1e3 ? (n / 1e3).toFixed(1) + 'k' : String(n);

function fmtReset(iso: string): string {
  const d = new Date(iso);
  const ms = d.getTime() - Date.now();
  if (ms <= 0) return 'a breve';
  const hhmm = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  if (ms < 24 * 3.6e6) {
    const h = Math.floor(ms / 3.6e6);
    const m = Math.round((ms % 3.6e6) / 6e4);
    return `tra ${h > 0 ? h + 'h ' : ''}${m}m (alle ${hhmm})`;
  }
  return `il ${d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })} alle ${hhmm}`;
}

const WINDOWS: { key: 'five_hour' | 'seven_day'; label: string }[] = [
  { key: 'five_hour', label: 'Finestra 5 ore' },
  { key: 'seven_day', label: '7 giorni' },
];

const PERIODS: { key: keyof UsageData['totals']; label: string }[] = [
  { key: 'today', label: 'Oggi' },
  { key: 'week', label: '7 giorni' },
  { key: 'month', label: '30 giorni' },
  { key: 'all', label: 'Totale' },
];

export function Usage() {
  const [, navigate] = useLocation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.get<UsageData>('/usage'),
    retry: false,
  });

  return (
    <div className="app">
      <CommandBar onSettings={() => navigate('/settings')} />
      <Crumb current="Utilizzo" onBack={() => navigate('/')} backLabel="Progetti" />
      <p className="usenote">
        <b>Limite del tuo piano</b> qui sotto — la stessa percentuale che vedi su claude.ai, valida
        per <b>tutto insieme</b>: Claude Code, claude.ai e 2P AI. Più in basso, i token consumati
        dalle sole sessioni di 2P AI.
      </p>

      {isLoading ? <div className="usenote">Carico…</div> : null}
      {error ? (
        <div className="note danger"><Icon name="error" /><span>Impossibile caricare l’utilizzo.</span></div>
      ) : null}

      {data ? (
        <>
          {data.plan && (data.plan.five_hour || data.plan.seven_day) ? (
            <div className="planbox">
              <div className="planh">Limite del piano <span className="planpill">CC + claude.ai + 2P AI</span></div>
              {WINDOWS.map(({ key, label }) => {
                const w = data.plan?.[key];
                if (!w || w.utilization == null) return null;
                const pct = Math.max(0, Math.min(100, Math.round(w.utilization)));
                return (
                  <div key={key} className="planrow">
                    <div className="pl"><span>{label}</span><span className="pp">{pct}%</span></div>
                    <div className="pbar"><i className={pct >= 90 ? 'hot' : pct >= 70 ? 'warn' : ''} style={{ width: pct + '%' }} /></div>
                    {w.resets_at ? <div className="pr">si resetta {fmtReset(w.resets_at)}</div> : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="usenote">Il <b>% del piano</b> non è leggibile adesso. Di solito è temporaneo: Anthropic sta <b>limitando le richieste</b> (rate-limit) oppure il <b>token di login dell'hub è da rinnovare</b>. Riprova tra poco; se persiste, va rifatto il login di Claude Code sull'hub.</div>
          )}

          <h3 className="useh">Token consumati da 2P AI</h3>
          <div className="usegrid">
            {PERIODS.map((p) => {
              const b = data.totals[p.key];
              return (
                <div key={p.key} className="usecard">
                  <div className="ul">{p.label}</div>
                  <div className="un">{fmt(b.tokens)}</div>
                  <div className="us">token ({b.sessions} sessioni)</div>
                  <div className="ud">in {fmt(b.input)} · out {fmt(b.output)} · cache {fmt(b.cache_read)}</div>
                </div>
              );
            })}
          </div>

          <h3 className="useh">Per modello</h3>
          <div className="usetable">
            {data.by_model.length === 0 ? (
              <div className="usenote">Nessuna sessione registrata finora.</div>
            ) : (
              data.by_model.map((m) => (
                <div key={m.model} className="userow">
                  <span className="um">{m.model || '—'}</span>
                  <span className="ut">{fmt(m.tokens)} token</span>
                  <span className="usn">{m.sessions} sess.</span>
                </div>
              ))
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
